import { Component, OnInit, Input, EventEmitter, Output, TemplateRef, ChangeDetectionStrategy } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { DateService } from 'src/app/services/date.service';
import { RetroreflectionService } from 'src/app/services/retroreflection.service';
import {
  DimensionInformationEnum,
  HorizontalSignalingEnum,
  RetroreflectionTypeEnum,
  SignalingQualityEnum,
  SignalingTypeEnum,
  ApplicationColorEnum,
} from 'src/enumerators';
import { HorizontalSignaling, HorizontalSignalingRetroreflection, HorizontalSignalingRetroreflectionMeasurement } from 'src/models';

@Component({
  selector: 'app-horizontal-information',
  templateUrl: './horizontal-information.component.html',
  styleUrls: ['./horizontal-information.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HorizontalInformationComponent implements OnInit {

  @Input() horizontalSignaling: HorizontalSignaling;
  @Input() currentSignIndex: number | undefined = undefined;
  @Input() contractRoadItems: any[];
  @Input() retroreflections;

  @Input() markPainting: any[];

  @Output() currentSignChange = new EventEmitter<({ sign: HorizontalSignaling, index: number })>();
  @Output() retroreflectionsChange = new EventEmitter<({ retroreflections, sign: HorizontalSignaling, index: number })>();

  financialSignalingValue: any;

  signalingQualityEnum = Object.values(SignalingQualityEnum);

  horizontalSignalingEnum = HorizontalSignalingEnum;

  applicationColorEnum: typeof ApplicationColorEnum = ApplicationColorEnum;

  signalingType: SignalingTypeEnum = SignalingTypeEnum.Horizontal;
  typeSignEnum: typeof SignalingTypeEnum = SignalingTypeEnum;

  switchRetro = true;
  retroTypeEnum: typeof RetroreflectionTypeEnum = RetroreflectionTypeEnum;

  modalsRef?: Array<BsModalRef> = [];
  measuresToBeEdited: any[] = [];

  constructor(
    private _modalService: BsModalService,
    private _retroreflectionService: RetroreflectionService,
    public dateService: DateService,
  ) { }

  ngOnInit(): void { }

  private emitSignChange() {
    this.currentSignChange.emit({ sign: this.horizontalSignaling, index: this.currentSignIndex });
  }

  private normalizeApplicationColorByCode() {
    if (this.horizontalSignaling?.horizontalCode !== HorizontalSignalingEnum.LCO) {
      this.horizontalSignaling.color = undefined;
    }
  }

  handleApplicationColor(value: ApplicationColorEnum) {
    this.horizontalSignaling.color = value;
    this.emitSignChange();
  }

  handleHorizontalCodeChange(code: HorizontalSignalingEnum) {
    this.horizontalSignaling.horizontalCode = code;
    this.normalizeApplicationColorByCode();
    this.emitSignChange();
  }

  handleWarranty(warranty) {
    if (this.horizontalSignaling) {
      this.horizontalSignaling.warranty = warranty;
      this.emitSignChange();
    }
  }

  handleDimensionInformation(event: {
    dimensionType: DimensionInformationEnum;
    dimension: number;
    dimensionX: number;
    dimensionY: number;
    units: number;
    diameter: number;
    sideLength: number;
    quantity: number;
    dash: number,
    spacing: number
  }) {
    this.horizontalSignaling.quantity = event.quantity;
    this.horizontalSignaling.dimension = event.dimension;
    this.horizontalSignaling.dimensionX = event.dimensionX;
    this.horizontalSignaling.dimensionY = event.dimensionY;
    this.horizontalSignaling.dimensionType = event.dimensionType;
    this.horizontalSignaling.dash = event.dash;
    this.horizontalSignaling.spacing = event.spacing;

    this.emitSignChange();
  }

  roadItemText() {
    const roadSignMarkingPaint = this.contractRoadItems.find((item) => item.id == this.horizontalSignaling.contractSignItemId);

    if (roadSignMarkingPaint) {
      return roadSignMarkingPaint.roadSignMarkingPaint.name;
    }
    else return '-';
  };

  // Aceita o novo estado direto, em vez de toggle manual. Garante sincronia
  // entre estado nativo do <input> e a propriedade switchRetro.
  setRetro(checked: boolean): void {
    this.switchRetro = checked;
    if (this.switchRetro) {
      // Preserva o shape { measurements: [...] }; uma desativação anterior
      // pode ter sobrescrito retroreflections como [], deixando .measurements
      // undefined e jogando TypeError em .length.
      if (!this.retroreflections || !Array.isArray(this.retroreflections.measurements)) {
        this.retroreflections = { measurements: [] };
      }
      if (!this.retroreflections.measurements.length) {
        this.retroreflections.measurements.push(
          new HorizontalSignalingRetroreflectionMeasurement({
            horizontalSignalingId: this.horizontalSignaling.id ? this.horizontalSignaling.id : undefined,
            createdAt: new Date(),
            updatedAt: new Date(),
          }),
        );
      }
    } else {
      this.retroreflections = { measurements: [] };
    }
  }

  // Compatibilidade com callers existentes; delega pro setRetro com o estado oposto.
  handleRetro(): void {
    this.setRetro(!this.switchRetro);
  }

  openModalG(template: TemplateRef<any>) {
    const modalRefAux: BsModalRef = this._modalService.show(template, {
      class: 'modal-dialog-centered modal-xl',
      backdrop: 'static',
    });
    this.modalsRef.push(modalRefAux);
  }

  closeModal() {
    const last: any = this.modalsRef.length - 1;
    this.modalsRef[last].hide();
    this.modalsRef.pop();
  }

  setMeasure() {
    if (this.retroreflections.measurements.length < 10) {
      if (this.retroreflections.measurements.length <= 1) {
        for (let i of [1, 2, 3, 4, 5]) {
          this.retroreflections.measurements.push(new HorizontalSignalingRetroreflectionMeasurement({}));
        }
      } else {
        this.retroreflections.measurements.push(new HorizontalSignalingRetroreflectionMeasurement({}));
      }
    }
  }

  emitRetro() {
    this.retroreflectionsChange.emit({ retroreflections: this.retroreflections, sign: this.horizontalSignaling, index: this.currentSignIndex });
  };

  removeMeasure(indexOfMeasure) {
    this.retroreflections.measurements.splice(indexOfMeasure, 1);
    this.setRetroAverage(this.retroreflections?.measurements);
  }

  setRetroAverage(measurements: any[]) {
    const measureArrayCopy = [...measurements];

    let measurementValues = measureArrayCopy.map((v) => {
      return parseInt(v.value);
    });

    if (measurements.length > 2) {
      const maxValue = Math.max(...measurementValues);
      const indexOfMax = measurementValues.indexOf(maxValue);
      measurementValues.splice(indexOfMax, 1);

      const minValue = Math.min(...measurementValues);
      const indexOfMin = measurementValues.indexOf(minValue);
      measurementValues.splice(indexOfMin, 1);
    }

    let retroValueSum = measurementValues.reduce((current, measure) => {
      return current + measure;
    }, 0);

    this.retroreflections.average = retroValueSum / measurementValues.length;
  }

  saveSegmentedRetro(segmentedMeasureList) {
    if (segmentedMeasureList.id) {
      this.updateSingleMeasure(this.horizontalSignaling.type, segmentedMeasureList.id, segmentedMeasureList);
    } else {
      this.retroreflections = new HorizontalSignalingRetroreflection({
        updatedAt: new Date(),
        type: RetroreflectionTypeEnum.Segmentado,
        horizontalSignalingId: this.horizontalSignaling.id,
        measurements: this.retroreflections.measurements.map((v) => v.Serialize()),
        average: this.retroreflections.average,
      });
    }
  }

  updateSingleMeasure(signType, measureId, retro) {
    this.measuresToBeEdited.push({ signType: signType, measureId: measureId, retro: retro });
  }

  submitSingleMeasures(retro) {
    this._retroreflectionService.editHorizontalRetro(retro).subscribe((next) => { });
  }

  setExecutionDate(date, signType) {
    this.horizontalSignaling.executionDate = date;
  }
}
